import { z } from 'zod';
/**
 * FlightAction — one scheduled / drafted / pending item on the agent's
 * forward 5-day flight plan (`/approvals/flight-plan`). Today the page
 * derives flight actions client-side from two sources (pending drafts +
 * future-dated `AgentAction`s); once the backend lands a dedicated
 * `agent_scheduled_actions` projection it will return rows shaped like
 * this. Living in `packages/shared` keeps server + client on the same
 * closed enums (CLAUDE.md "closed string sets are enums" rule).
 *
 * The companion presentation primitive lives at
 * `apps/web/src/components/agent-transparency/flight-week-grid.tsx`
 * and consumes these types verbatim.
 */
/**
 * Risk level — closed set, drives the per-card left-border color in the
 * `<FlightWeekGrid>`.
 *
 *  - `none`        — agent acting within autonomy budget, no human gate
 *  - `conditional` — conditional autonomy (bulk-approvable / routine)
 *  - `approval`    — explicit CEO sign-off required before send
 */
export const FlightRisk = z.enum(['none', 'conditional', 'approval']);
/**
 * Action kind — closed set, drives the per-card eyebrow tag color +
 * label. Mirrors the designer's five kinds; the page maps from
 * `AgentActionKind` (drafted → drafting, scheduled → scheduled/auto by
 * status, etc.) via a closed switch at the call site.
 */
export const FlightTag = z.enum(['scheduled', 'drafting', 'auto', 'conditional', 'approval']);
/**
 * FlightAction — wire-shape for one card in the forward flight plan.
 * `time` is pre-formatted by the caller (display-local "9:00 AM") so
 * the grid stays presentation-only.
 */
export const FlightAction = z.object({
    id: z.string(),
    /** Display time (e.g. "9:00 AM"). Pre-formatted by the caller. */
    time: z.string(),
    title: z.string(),
    /** Sub-line (e.g. "Slack DM · 7 recipients" / "Pending your approval"). */
    who: z.string(),
    risk: FlightRisk,
    tag: FlightTag,
});
/**
 * FlightDay — one column (one weekday) of the Mon..Fri flight-plan grid.
 * `day` + `dateLabel` are pre-formatted display strings so date-math
 * lives in exactly one place (the page).
 */
export const FlightDay = z.object({
    /** Short weekday label (e.g. "Mon"). */
    day: z.string(),
    /** Display date suffix (e.g. "MAY 18"). */
    dateLabel: z.string(),
    items: z.array(FlightAction).readonly(),
});
